/**
 * ops-adapter.ts — 运营位真实 API DTO → 原型 mock 行的塑形纯函数
 *
 * 把后端 snake_case 规范化 DTO（HomeSectionResponse / BannerResponse / BannerSlideResponse /
 * FaqResponse，见 packages/shared/src/schemas/ops.ts）塑形成原型设计稿用的 camelCase 富集行
 * （Banner / HomeBlock / Faq，见 ~/data/mock/types.ts），保证页面零改、100% 设计还原。
 *
 * 塑形约定：
 *  - i18n 字段（title_i18n / question_i18n / category_i18n）取 `zh || en` 做中文展示；
 *  - is_active(bool) → 中文「上架 / 下架」（原型 status 是中文枚举字符串）；
 *  - recommend_strategy(枚举) → 中文区块类型（原型有此展示，DTO 只给枚举）；
 *  - langs（语言覆盖数）= i18n record 的 locale 数量（DTO 不单独给计数，按已录入语言数推导）。
 *
 * 缺字段处理（真 API 普遍缺 mock 的聚合 / 关联名字段）——逐项注释标注来源：
 *  - Banner.app（App 名）：DTO 只给 app_id(UUID)，名字需外部解析 → 由调用方传入
 *    `appNameById` 解析表（来自 useApps()），缺省回退原始 app_id；
 *  - Banner.slides（轮播张数）：BannerResponse 无该计数 → N+1 取 opsApi.listSlides(id).length
 *    后由调用方传入 `slideCountById`，缺省回退 0；
 *  - Banner.position（展示位）：DTO 只给 slug，无独立 position 字段 → 用 slug→中文 映射，
 *    未命中回退 slug 原值；
 *  - Banner.updated（更新时间）：**已接真**——取 BannerResponse.updated_at（ISO，banners.updated_at）经
 *    fmtDate 裁成 YYYY-MM-DD 按日粒度展示（OpsPage 条件渲染「· 更新 X」）。
 */

import type {
  BannerResponse,
  BannerSlideResponse,
  FaqCategoryResponse,
  FaqResponse,
  HomeSectionResponse,
  OfferServingState,
  PopupPlacementPaymentMethod,
  PopupPlacementResponse,
} from '@novel-admin/shared';
import { SETTLEMENT_CHANNELS } from '@novel-admin/shared';
import type { Banner, Faq, HomeBlock } from '~/data/mock/types';
import { fmtDate } from '~/lib/format';

/** i18n 字段取中文展示值：优先 zh，回退 en，再回退任意首个非空值，全空回退 ''。 */
function pickI18n(i18n: Record<string, string>): string {
  const zh = i18n.zh;
  if (zh) return zh;
  const en = i18n.en;
  if (en) return en;
  for (const key of Object.keys(i18n)) {
    const v = i18n[key];
    if (v) return v;
  }
  return '';
}

/** i18n record 的有效语言数（非空值计数）—— 充当原型的 langs（语言覆盖数）。 */
function countLocales(i18n: Record<string, string>): number {
  let n = 0;
  for (const key of Object.keys(i18n)) {
    if (i18n[key]) n += 1;
  }
  return n;
}

/** is_active(bool) → 原型中文 status 枚举。 */
function statusLabel(isActive: boolean): string {
  return isActive ? '上架' : '下架';
}

/**
 * strategy_key → 中文区块类型（张培 #101：section_id 实例 id / strategy_key 策略 key 拆分）。
 * strategy_key 是 server 注册表的策略 key（如 weekly_featured / for_you / new_releases）；未命中回退原始
 * key（前端兼容未来新增策略）。
 */
const STRATEGY_KEY_LABEL: Record<string, string> = {
  weekly_featured: '每周精选',
  for_you: '猜你喜欢',
  new_releases: '新书上架',
};

export function strategyKeyLabel(strategyKey: string): string {
  return STRATEGY_KEY_LABEL[strategyKey] ?? strategyKey;
}

/**
 * Banner slug → 原型展示位中文名。
 * 原型 position 是「书城首页 / 充值页」一类的中文展示位，DTO 只给 slug，按约定 slug 映射；
 * 未命中回退 slug 原值（设计稿仅作文本展示，不影响布局）。
 */
const BANNER_POSITION_LABEL: Record<string, string> = {
  home: '书城首页',
  bookstore: '书城首页',
  bookstore_home: '书城首页',
  topup: '充值页',
  recharge: '充值页',
};

/**
 * 是否系统默认栏（含 App override，bot #19）：默认栏 section_id === strategy_key（bare key，如 weekly_featured）；
 * 自定义栏 section_id 恒为 `${strategy_key}_<hex>` ≠ strategy_key。App override 行 `is_seed_baseline=false` 但
 * section_id 复用默认栏 key（== strategy_key），故按本判据仍归「系统」—— 否则 App 级下架/改写的默认栏会被误显示
 * 为「自定义」，与运营新建区块无从区分。`is_seed_baseline` 作为全局 seed 行的显式兜底一并纳入。
 */
function isDefaultHomeSection(dto: HomeSectionResponse): boolean {
  return dto.is_seed_baseline || dto.section_id === dto.strategy_key;
}

/**
 * fromHomeSection — HomeSectionResponse → 原型 HomeBlock。
 *  title         ← title_i18n(zh||en)
 *  type          ← 系统（默认栏 + App override）/ 自定义（见 isDefaultHomeSection）
 *  strategyKey   ← strategy_key（策略列从此渲染，不再按旧 mock id 分支；bot #19）
 *  strategyLabel ← strategy_key 中文名
 *  sort          ← display_order
 *  status        ← is_active → 上架/下架
 *  langs         ← title_i18n 的语言数（DTO 无独立 langs 计数）
 */
export function fromHomeSection(dto: HomeSectionResponse): HomeBlock {
  return {
    // layout 原样映射，不由平台或策略推断。
    layout: dto.layout,
    id: dto.id,
    title: pickI18n(dto.title_i18n),
    type: isDefaultHomeSection(dto) ? '系统' : '自定义',
    strategyKey: dto.strategy_key,
    strategyLabel: strategyKeyLabel(dto.strategy_key),
    sort: dto.display_order,
    status: statusLabel(dto.is_active),
    langs: countLocales(dto.title_i18n),
    // 接真原始字段（写动作 upsert/delete 用，不凭展示值杜撰契约——checklist #4）：
    appId: dto.app_id,
    language: dto.language,
    sectionId: dto.section_id,
    titleI18n: dto.title_i18n,
    readingPreferences: dto.reading_preferences,
    // 删除拦截以 is_seed_baseline 为准（系统 baseline seed 全局栏禁删，ADM-OPS-S001）
    isSeed: dto.is_seed_baseline,
    isActive: dto.is_active,
  };
}

/** 解析 Banner 关联名字段所需的外部上下文（缺则各自回退）。 */
export interface BannerAdaptContext {
  /** app_id(UUID) → App 名，来自 useApps()；缺省回退 app_id。 */
  appNameById?: Record<string, string>;
  /** banner_id → 轮播张数，来自 N+1 opsApi.listSlides(id).length；缺省回退 0。 */
  slideCountById?: Record<string, number>;
}

/**
 * fromBanner — BannerResponse → 原型 Banner。
 *  app      ← appNameById[app_id]（缺省回退 app_id 原值，DTO 不带 App 名）
 *  position ← name（人类可读位置名；name 空时回退 slug→中文展示位、再回退 slug）
 *  name     ← name 原值（PATCH /ops/banners/:id 的 name 回写用，position 是展示别名不能当 name 写回）
 *  slides   ← slideCountById[id]（缺省 0，BannerResponse 无 slide 计数，靠 N+1 listSlides）
 *  status   ← is_active → 上架/下架
 *  updated  ← fmtDate(dto.updated_at)（banners.updated_at ISO → 裁成 YYYY-MM-DD，按日粒度展示；已接真）
 */
export function fromBanner(dto: BannerResponse, ctx: BannerAdaptContext = {}): Banner {
  // app_id 为 null = 全局默认位（所有 App 共享，#104 global∪perApp）→ 展示「全部 App」；非空 → 解析 App 名（缺省回退 id）
  const appName = dto.app_id === null ? '全部 App' : (ctx.appNameById?.[dto.app_id] ?? dto.app_id);
  const slides = ctx.slideCountById?.[dto.id] ?? 0;
  // 展示位置名优先用 name（人类可读），name 空再回退 slug→中文映射、再回退 slug
  const position = dto.name || BANNER_POSITION_LABEL[dto.slug] || dto.slug;
  return {
    id: dto.id,
    app: appName,
    position,
    slides,
    status: statusLabel(dto.is_active),
    // updated 接真：banners.updated_at（ISO）→ 裁成 YYYY-MM-DD（Banner 更新按日粒度展示，OpsPage 条件渲染「· 更新 X」）
    updated: fmtDate(dto.updated_at),
    // 接真原始字段（写动作 upsert/delete 用）：
    appId: dto.app_id,
    language: dto.language,
    slug: dto.slug,
    name: dto.name,
    // 删除拦截以 is_seed_baseline 为准（系统预置全局位禁删，仅可上下架）
    isSeed: dto.is_seed_baseline,
    isActive: dto.is_active,
  };
}

/**
 * slideCountFromSlides — 把某 Banner 的 N+1 slide 列表折算成张数，喂给 BannerAdaptContext.slideCountById。
 * 调用方对每个 banner 取一次 opsApi.listSlides(bannerId) 后用本函数计数（原型 Banner.slides = 轮播张数）。
 */
export function slideCountFromSlides(slides: BannerSlideResponse[]): number {
  return slides.length;
}

/**
 * offer_serving_state 闭集 → 徽章文案素材（A2 §4.1；仅 target_type='offer' 行有值）。
 * chip 档位复用既有 token 原子类（pos/warn/neg），不引新视觉。
 *
 * 拆成 `reason`（offer 侧为什么是这个态）与 `next`（运营下一步该干什么）两段而不是一整句 hint：
 * **下一步必须能被「slide / 位下架」这层改写**——见 `offerServingBadge`。整句写死就没法叠加，
 * 于是会出现「到点自动展示，无需操作」这种在下架行上根本走不通的指引。
 *
 * ⚠️ 本表只是 **offer 侧**的判定，别直接拿去渲染——渲染一律走 `offerServingBadge`。
 */
export const OFFER_SERVING_STATE_TEXT: Record<
  OfferServingState,
  {
    label: string;
    chip: string;
    reason: string;
    /** 两级都上架时的下一步（null = 无需操作）。 */
    next: string | null;
    /** 有层级下架时的下一步；`up` 是「上架」/「两处都上架」。整句重写而非拼接—— */
    /** `NOT_STARTED` 的「无需操作」与「还得上架」直接冲突，拼在一起就是自相矛盾的指引。 */
    nextBlocked: (up: string) => string;
  }
> = {
  SERVING: {
    label: '展示中',
    chip: 'chip-pos',
    reason: '被引运营活动在有效期内且已启用',
    next: null, // 正在展示，无需操作
    nextBlocked: (up) => `${up}后即展示`,
  },
  NOT_STARTED: {
    label: '未开始',
    chip: 'chip-warn',
    reason: '被引运营活动尚未到开始时间',
    next: '到点自动展示，无需操作',
    nextBlocked: (up) => `到点后还需${up}才展示`,
  },
  ENDED: {
    label: '已结束',
    chip: 'chip-neg',
    reason: '被引运营活动已过结束时间',
    next: '需改活动时间或换一个活动',
    nextBlocked: (up) => `需改活动时间或换一个活动，并${up}`,
  },
  OFFER_UNAVAILABLE: {
    label: '活动失效',
    chip: 'chip-neg',
    reason: '被引运营活动不存在 / 已停用 / 已删除 / 不属本 App',
    next: '需换一个有效活动',
    nextBlocked: (up) => `需换一个有效活动，并${up}`,
  },
};

/** 挡住展示的上下架层级（slide / 所属位），按运营看得懂的措辞列出；两处都下架就两处都列。 */
function offlineBlockers(slideActive: boolean, bannerActive: boolean): string[] {
  return [
    ...(slideActive ? [] : ['本轮播图已下架']),
    ...(bannerActive ? [] : ['所属 Banner 位已下架']),
  ];
}

/**
 * 全局 Banner 位（`app_id IS NULL`）上挂 offer slide 时的专用指引。
 *
 * Why（GitHub bot P2）：这种行必然判 `OFFER_UNAVAILABLE`，但通用指引「需换一个有效活动」**在这里永远
 * 走不通**——写侧 `assertActiveOfferForApp` 对 `bannerAppId === null` 无条件拒（运营活动按 App 划分，
 * 全局位没有单一 App 可绑），换哪个活动都撞 `OPS_BANNER_OFFER_NOT_FOUND`。真正能做的只有：删掉这条
 * 轮播图 / 改成别的跳转类型，或到某个 App 的 Banner 位下重建。
 *
 * 这种行怎么来的：写侧只对「结果 is_active=true」的 slide 校验 offer，所以**下架态**的 offer slide
 * 能落在全局位上（早期数据 / 手工改库同理）。
 */
const GLOBAL_BANNER_OFFER_TEXT = {
  reason: '全局 Banner 位不能挂运营活动（活动按 App 划分，全局位没有单一 App 可绑）',
  next: '需删除本轮播图或改成别的跳转类型；要投放本活动请到对应 App 的 Banner 位下重建',
};

/**
 * 合成一枚**不会说谎、且下一步走得通**的徽章。
 *
 * 背景：`offer_serving_state` 只由被引 offer 判定（契约 A2 §4.1 就是这么定义的），而 C 端是否真的展示
 * 还要求**本轮播图上架 + 所属 Banner 位上架**（数据字典 §3.3.3「位集合合并」+「轮播图填充」两条）。
 * 两层信息不叠加，会连着犯三种错：
 *
 * 1. **说反事实**（GitHub bot P2）：`SERVING` 直接渲染，清单里就会并排出现「下架」和「展示中」，
 *    悬停还写着「C 端正在展示」。→ 降级成「活动进行中」（而不是干脆不渲染：运营需要知道「活动本身是
 *    好的，只差我上架」，这正是上架后立刻生效、还是上架了也白搭的区别）。
 * 2. **给走不通的下一步**（codex CLI P3 + bot P2）：`NOT_STARTED` 的「到点自动展示，无需操作」在下架行
 *    上是假的——到点了也不展示；`ENDED` 的「改活动时间」同理，改完仍然不展示。而只报一个拦路条件
 *    （两处都下架却只说 slide）会让运营上架完发现还是没用。→ 拦路条件全列，并把「上架」并进下一步。
 * 3. **给必然失败的下一步**（GitHub bot P2）：全局位上的 offer slide 判 `OFFER_UNAVAILABLE`，通用指引
 *    「换一个有效活动」换哪个都被写侧拒。→ 见 `GLOBAL_BANNER_OFFER_TEXT`，整句换成真正做得到的动作。
 */
export function offerServingBadge(
  state: OfferServingState,
  ctx: { slideActive: boolean; bannerActive: boolean; bannerIsGlobal: boolean },
): { label: string; chip: string; hint: string } {
  const t = OFFER_SERVING_STATE_TEXT[state];
  const blockers = offlineBlockers(ctx.slideActive, ctx.bannerActive);
  const blocked = blockers.length > 0;
  // 全局位挂 offer 是配置本身不成立，压过 offer 侧的四态成因（换活动救不了它）
  const globalOfferMisconfig = ctx.bannerIsGlobal;

  const reasons = [globalOfferMisconfig ? GLOBAL_BANNER_OFFER_TEXT.reason : t.reason, ...blockers]
    // 全局位这条已经自成一句完整判断，再跟上下架拦路条件时用「；」分隔更清楚
    .join(globalOfferMisconfig ? '；' : '，');
  const fact =
    state === 'SERVING' && !blocked && !globalOfferMisconfig
      ? 'C 端正在展示本轮播图'
      : 'C 端当前不展示本轮播图';
  // 两处都下架必须说「两处都上架」：只报一处，运营上架完会发现还是不展示。
  // 全局位的指引不叠「上架」——那条路本来就不通，上架只会撞写侧校验。
  const step = globalOfferMisconfig
    ? GLOBAL_BANNER_OFFER_TEXT.next
    : blocked
      ? t.nextBlocked(blockers.length > 1 ? '两处都上架' : '上架')
      : t.next;

  return {
    // 活动本身没问题、只是下架挡着 → 标签不能是「展示中」
    label: globalOfferMisconfig
      ? '活动失效'
      : state === 'SERVING' && blocked
        ? '活动进行中'
        : t.label,
    chip: globalOfferMisconfig ? 'chip-neg' : state === 'SERVING' && blocked ? 'chip-warn' : t.chip,
    hint: `${reasons} · ${fact}${step ? `（${step}）` : ''}`,
  };
}

/** target_type 闭集 → 原型展示中文（book/offer/web，#228 决议 3：topup→offer）。 */
const SLIDE_TARGET_LABEL: Record<string, string> = {
  book: '书籍',
  offer: '运营活动',
  web: '链接',
};

/**
 * fromSlide — BannerSlideResponse → BannerSlidesTab / NewSlideDialog 用的 Slide（展示 + 接真原始字段）。
 *  title/en   ← title_i18n(zh / en)（轮播标题展示）
 *  target     ← target_type 中文（书籍/充值/链接）
 *  to         ← target_value（寻址原值，列表里截断展示）
 *  hue        ← 由 id charCode 派生（纯展示用渐变色，无契约字段）
 *  status     ← is_active → 上架/下架
 *  langs      ← title_i18n 语言数
 *  imageUrl   ← image_url（读侧鉴权代理相对路径，A7 §2.2 口径——authedFetch→blob 渲染真图预览）
 *  offerServingState ← offer_serving_state（A2 §4.1 派生只读态，**仅 offer 行有值**，book/web 为 undefined）：
 *    与 status/isActive 正交——status 是运营的上下架意图，本字段是「C 端此刻是否真在展示」（活动没开始 /
 *    已结束 / offer 失效时，即便已上架也不展示）。列表按它出「不展示」提示。
 *  接真原始字段（编辑/删除/上下架回传，避免凭展示值杜撰契约——checklist #4）：titleI18n / targetType /
 *  targetValue / imageR2Key / displayOrder / isActive。
 */
export function fromSlide(dto: BannerSlideResponse): {
  id: string;
  title: string;
  en: string;
  target: string;
  to: string;
  hue: number;
  status: string;
  langs: number;
  titleI18n: Record<string, string>;
  targetType: (typeof dto)['target_type'];
  targetValue: string;
  openMode: (typeof dto)['open_mode'];
  imageR2Key: string;
  imageUrl: string;
  displayOrder: number;
  isActive: boolean;
  offerServingState: BannerSlideResponse['offer_serving_state'];
} {
  const lastChar = dto.id.charCodeAt(dto.id.length - 1) || 0;
  return {
    id: dto.id,
    title: dto.title_i18n.zh ?? dto.title_i18n.en ?? '',
    en: dto.title_i18n.en ?? dto.title_i18n.zh ?? '',
    target: SLIDE_TARGET_LABEL[dto.target_type] ?? dto.target_type,
    to: dto.target_value,
    hue: (lastChar * 37) % 360,
    status: statusLabel(dto.is_active),
    langs: countLocales(dto.title_i18n),
    titleI18n: dto.title_i18n,
    targetType: dto.target_type,
    targetValue: dto.target_value,
    openMode: dto.open_mode,
    imageR2Key: dto.image_r2_key,
    imageUrl: dto.image_url,
    displayOrder: dto.display_order,
    isActive: dto.is_active,
    offerServingState: dto.offer_serving_state,
  };
}

/**
 * FAQ 分类 baseline key → 中文兜底展示名（#221：category 升级为可配置 faq_categories 注册表）。
 * **仅兜底**——权威展示名来自 faq_categories.title_i18n（运营可增删 / 改名）；分类无 title 或前端拿不到
 * 分类表时回退本 map（对齐 shared FAQ_BASELINE_CATEGORY_KEYS 6 条基线），再回退 key 原值。
 */
const FAQ_BASELINE_CATEGORY_LABEL: Record<string, string> = {
  PAYMENT: '充值 / 退款',
  CONTENT: '书籍 / 章节',
  BUG: '功能异常',
  SUGGESTION: '建议',
  ACCOUNT: '账号',
  OTHER: '其他',
};

/**
 * FAQ 分类展示名：title_i18n(zh||en) 优先，回退 baseline 中文兜底，再回退 key 原值。
 * category_key 只是稳定键（无展示名），故 fromFaq 单看 FAQ 行只能给 baseline/key 兜底；
 * 权威分类名由消费方（useFaqCategories）注入的 title_i18n 决定（见 buildFaqCategoryOptions）。
 */
export function faqCategoryTitle(key: string, titleI18n?: Record<string, string>): string {
  if (titleI18n) {
    const t = pickI18n(titleI18n);
    if (t) return t;
  }
  return FAQ_BASELINE_CATEGORY_LABEL[key] ?? key;
}

/** FAQ 分类展示行（faq_categories 注册表塑形；分类管理 / 下拉候选 / 分组表头标签共用）。 */
export interface FaqCategoryRow {
  id: string;
  /** 稳定键 UPPER_SNAKE（faq.category_key FK 锚点；创建后不可改）*/
  key: string;
  /** 展示名 = title_i18n(zh||en) || baseline 兜底 || key */
  label: string;
  /** title_i18n 原始多语言池（编辑分类名回填用）*/
  titleI18n: Record<string, string>;
  displayOrder: number;
  isActive: boolean;
  /** server baseline seed 预置（6 条基线分类禁删）；admin 只读 */
  isSeedBaseline: boolean;
}

/**
 * fromFaqCategory — FaqCategoryResponse → FaqCategoryRow（#221 分类注册表）。
 *  label ← title_i18n(zh||en) || baseline 兜底 || key（唯一展示名派生口径）
 */
export function fromFaqCategory(dto: FaqCategoryResponse): FaqCategoryRow {
  return {
    id: dto.id,
    key: dto.key,
    label: faqCategoryTitle(dto.key, dto.title_i18n),
    titleI18n: dto.title_i18n,
    displayOrder: dto.display_order,
    isActive: dto.is_active,
    isSeedBaseline: dto.is_seed_baseline,
  };
}

/**
 * 由 live 分类行构建下拉候选 `{ value: key, label }`：FAQEditor 分类选择器 + OpsPage 分组表头据此渲染，
 * 提交回传 value（= category_key 稳定键，非展示名）。label 已在 fromFaqCategory 派生（title_i18n || 兜底）。
 */
export function buildFaqCategoryOptions(
  cats: FaqCategoryRow[],
): { value: string; label: string }[] {
  return cats.map((c) => ({ value: c.key, label: c.label }));
}

/**
 * fromFaq — FaqResponse → 原型 Faq（#221 canonical：可配置 category_key + global∪perApp + is_seed_baseline）。
 *  categoryKey   ← category_key（FK→faq_categories.key，稳定键）
 *  categoryLabel ← baseline 兜底展示名（权威名由 OpsPage 从 useFaqCategories 注入，见 §7）
 *  question ← question_i18n(zh||en)
 *  langs    ← question_i18n 的语言数（与原型「语言覆盖数」语义一致，问题即条目本体）
 *  sort     ← display_order
 *  status   ← is_active → 上架/下架
 */
export function fromFaq(dto: FaqResponse): Faq {
  return {
    id: dto.id,
    categoryKey: dto.category_key,
    categoryLabel: faqCategoryTitle(dto.category_key),
    question: pickI18n(dto.question_i18n),
    langs: countLocales(dto.question_i18n),
    sort: dto.display_order,
    status: statusLabel(dto.is_active),
    // 接真原始字段（写动作用；category_key 回传稳定键非展示名，slug/app_id 不可改故不回传）：
    isSeedBaseline: dto.is_seed_baseline,
    appId: dto.app_id,
    language: dto.language,
    questionI18n: dto.question_i18n,
    answerI18n: dto.answer_i18n,
    isActive: dto.is_active,
  };
}

// ---------- 运营弹窗 Paywall 落点（ADM-OPS-S008，A2 §6）----------

/** 落点渠道展示名；按结算通道建表，闭集新增通道时编译期提示补齐 */
const POPUP_PLACEMENT_CHANNEL_LABEL: Record<PopupPlacementPaymentMethod, string> = {
  IAP: '应用内购（IAP）',
  STRIPE: '第三方支付（Stripe）',
  PAYERMAX: '第三方支付（PayerMax）',
};

/**
 * 渠道闭集 = 结算通道层 `SETTLEMENT_CHANNELS`（对齐 `app_payment_methods` 的 active CHECK，含已登记框架通道；
 * 与支付页渠道卡同源）。界面恒按闭集逐行渲染、不提供新建 / 删除动词 —— 与本 Tab 场景表同一心智。
 */
export const POPUP_PLACEMENT_CHANNELS: ReadonlyArray<{
  value: PopupPlacementPaymentMethod;
  label: string;
}> = SETTLEMENT_CHANNELS.map((value) => ({ value, label: POPUP_PLACEMENT_CHANNEL_LABEL[value] }));

/** 一个渠道在当前 App 下的落点状态（恒存在，未建行 / 未填值都算「未配置」）。 */
export interface PopupPlacementChannelRow {
  paymentMethod: PopupPlacementPaymentMethod;
  label: string;
  /** 该渠道在本 App 下已建的行；`undefined` = 未建行（保存要走 POST 而非 PATCH）*/
  row?: PopupPlacementResponse;
  /** 已存的 placement 值；`''` = 未配置（未建行，或建了行但 `adapty_placement_id` 为 null）*/
  placementId: string;
  /**
   * 是否参与 serving。判据只有一个：`adapty_placement_id` 非空（§6.2 渠道行无启停开关）。
   * 「已建行但未填值」与「未建行」对 C 端等价，故两者同归 false —— 界面据此给空态而不是只留空输入框。
   */
  configured: boolean;
}

/**
 * placement DTO 列表 → 渠道闭集两行（缺行补空态）。
 *
 * 服务端按 `(app_id, payment_method)` 至多一行未软删（partial unique），但列表端点本身不保证只回
 * 两行（未带 app_id 过滤时是全 App 的），故这里按 payment_method 取**首个命中**、并丢弃闭集外的值：
 * 宁可少渲染一个未知通道，也不把它当成运营可配的第三行画出来。
 */
export function toPopupPlacementChannelRows(
  dtos: PopupPlacementResponse[],
): PopupPlacementChannelRow[] {
  return POPUP_PLACEMENT_CHANNELS.map(({ value, label }) => {
    const row = dtos.find((d) => d.payment_method === value);
    return {
      paymentMethod: value,
      label,
      row,
      placementId: row?.adapty_placement_id ?? '',
      configured: row?.adapty_placement_id != null && row.adapty_placement_id !== '',
    };
  });
}
